import type {ReactNode} from 'react';
import {cx} from './cx';

/**
 * A screen that is the end of a road: order sent, QR invalid, venue closed.
 *
 * The design draws exactly one of these — the success screen — and it is the
 * pattern the two undrawn ones are extrapolated from. Its vocabulary: no top
 * bar, no scroll, centred content, a 56px ring with a glyph, a headline, a line
 * of body copy, a quieter supporting line, then the ways out in a footer.
 *
 * # Tones, and the one that does not exist here
 *
 * * `info` — Butterfly Pea. The order reached the kitchen. This is the "done"
 *   colour that is **not** money, and the design's success tick already
 *   establishes it.
 * * `warning` — Palm Sugar. The venue is closed; nothing is broken.
 * * `error` — Dried Chili. The QR cannot be used.
 *
 * There is no `verified` tone and there will not be one. Pandan states that
 * pokpok's own server acknowledged money; a terminal *notice* never does. A paid
 * result renders a `Card tone="verified"` inside this shell, with the tone
 * decided by `src/order/moneyTone.ts`.
 *
 * # No red CTA unless there is genuinely somewhere forward to go
 *
 * The red rule is one solid forward action per screen, and a closed venue has no
 * forward action — a red button that reloads a page which will say the same
 * thing is a promise the screen cannot keep. Render the ways out as `secondary`
 * or `tertiary`, and keep the pokpok attribution line: it is the only thing on
 * the page that tells a stuck guest what they were even using.
 *
 * ```tsx
 * <Screen>
 *   <ScreenBody center>
 *     <TerminalNotice tone="warning" glyph="!" title={t('q20.title')}>
 *       <p>{t('q20.body')}</p>
 *       <p className="pk-hint">{t('q20.reopensAt', {time: time(opensAt)})}</p>
 *     </TerminalNotice>
 *   </ScreenBody>
 *   <ScreenFooter bordered={false}>…</ScreenFooter>
 * </Screen>
 * ```
 */
export function TerminalNotice({
  tone,
  glyph,
  title,
  children,
  className,
}: {
  tone: 'info' | 'warning' | 'error';
  /**
   * One character, decorative and `aria-hidden` — a tick, an exclamation, a
   * cross. The headline carries the meaning; colour and glyph only reinforce it
   * (foundations §12).
   */
  glyph: string;
  /** Localised. Rendered as the screen's `h1`. */
  title: ReactNode;
  /** Body copy, and any supporting lines. */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('pk-terminal', className)}>
      <span className={cx('pk-terminal-ring', `pk-terminal-ring--${tone}`)} aria-hidden="true">
        {glyph}
      </span>
      <h1 className="pk-title">{title}</h1>
      {children}
    </div>
  );
}
